import instance from "../Api/Instance";
import { back } from "../Api/ApiUrl";
import { useState } from "react";
import { Modal } from "antd";
import moment from "moment";
import { useDispatch } from "react-redux";
import {
  setMaterialRequestHistoryLoading,
  setMaterialRequestHistoryResults,
} from "../redux/slices/materialRequestHistorySlice";

const initialMaterialLimit = {
  isLoading: false,
  data: null,
  status: "idle",
  statusCode: 0,
  message: "",
  errors: [],
};

const materialRequestErrorModal = {
  title: "Error en la petición de material",
  content:
    "Error al realizar la petición de material. Por favor, inténtelo de nuevo más tarde.",
};

const materialRequestSuccessModal = {
  content: "Petición de material realizada con éxito.",
  okText: "Aceptar",
};

const requestHistoryEmptyMessage =
  "No se han encontrado resultados para la búsqueda realizada.";

const requestHistoryErrorMessage =
  "Se ha producido un error, inténtelo de nuevo.";

const buildMaterialLimitLoadingState = () => ({
  isLoading: true,
  data: [],
  status: "loading",
  statusCode: 0,
  message: "Cargando...",
  errors: [],
});

const buildMaterialLimitSuccessState = (response) => ({
  isLoading: false,
  data: response.data,
  status: "loaded",
  statusCode: response.status,
  message: "",
  errors: [],
});

const buildMaterialLimitErrorState = (errorOrResponse) => ({
  isLoading: false,
  data: [],
  status: "error",
  statusCode: 500,
  message: requestHistoryErrorMessage,
  errors:
    errorOrResponse?.response?.data?.errors ||
    errorOrResponse?.data?.errors ||
    [],
});

const buildRequestHistoryParams = ({
  contractNumber,
  clientNumber,
  labellerCode,
  startDate,
  endDate,
  size = 10,
  page = 1,
}) => ({
  contractNumber,
  clientNumber,
  labellerCode,
  startDate: startDate ? moment(startDate).format("DD-MM-YYYY") : null,
  endDate: endDate ? moment(endDate).format("DD-MM-YYYY") : null,
  size,
  page: page - 1,
});

const buildEmptyRequestHistoryState = ({ size = 10, page = 1 }) => ({
  data: [],
  status: "loaded",
  statusCode: 204,
  message: requestHistoryEmptyMessage,
  errors: [],
  size,
  page,
  totalResults: 0,
  totalPages: 0,
});

const buildRequestHistorySuccessState = (response, fallback) => {
  if (response.status === 204) {
    return buildEmptyRequestHistoryState(fallback);
  }

  const materialRequestsList = response.data?.materialRequestsList;
  const data = materialRequestsList?.data || [];

  return {
    data,
    status: "loaded",
    statusCode: data.length > 0 ? 200 : 204,
    message: data.length ? "" : requestHistoryEmptyMessage,
    errors: [],
    size: materialRequestsList?.pageSize || fallback.size,
    page: (materialRequestsList?.pageNumber || 0) + 1,
    totalResults: materialRequestsList?.totalResults || 0,
    totalPages: materialRequestsList?.totalPages || 0,
  };
};

const buildRequestHistoryErrorState = (error, fallback) => {
  const statusCode = error.response?.status || 500;

  return {
    data: [],
    status: "loaded",
    statusCode,
    message:
      statusCode === 204
        ? requestHistoryEmptyMessage
        : requestHistoryErrorMessage,
    errors: error.response?.data?.errors || [],
    size: fallback.size,
    page: 1,
    totalResults: 0,
    totalPages: 0,
  };
};

const showMaterialRequestErrorModal = () =>
  Modal.error(materialRequestErrorModal);

const showMaterialRequestSuccessModal = (onOk) =>
  Modal.success({
    ...materialRequestSuccessModal,
    onOk,
  });

export const useMaterialRequest = () => {
  const [materialLimit, setMaterialLimit] = useState(initialMaterialLimit);
  const dispatch = useDispatch();

  const resetMaterialLimit = () => {
    setMaterialLimit(initialMaterialLimit);
  };

  const fetchMaterialLimit = async (
    contractNumber,
    clientNumber,
    labellerCode,
  ) => {
    setMaterialLimit(buildMaterialLimitLoadingState());

    try {
      const response = await instance.get(back.getMaterialLimits, {
        params: {
          contractNumber,
          clientNumber,
          labellerCode,
        },
      });

      if (response.status !== 200) {
        setMaterialLimit(buildMaterialLimitErrorState(response));
        return;
      }

      setMaterialLimit(buildMaterialLimitSuccessState(response));
    } catch (error) {
      setMaterialLimit(buildMaterialLimitErrorState(error));
    }
  };

  const createMaterialRequest = async (
    formData,
    onSuccess,
    onBeforeRequest,
    onAfterRequest,
  ) => {
    if (typeof onBeforeRequest === "function") {
      onBeforeRequest();
    }

    try {
      const response = await instance.post(
        back.createMaterialRequest,
        formData,
      );

      if (typeof onAfterRequest === "function") {
        onAfterRequest();
      }

      if (response.status !== 201) {
        return showMaterialRequestErrorModal();
      }

      return showMaterialRequestSuccessModal(() => {
        resetMaterialLimit();

        if (typeof onSuccess === "function") {
          onSuccess();
        }
      });
    } catch {
      if (typeof onAfterRequest === "function") {
        onAfterRequest();
      }

      return showMaterialRequestErrorModal();
    }
  };

  const fetchRequestHistory = async ({
    contractNumber,
    clientNumber,
    labellerCode,
    startDate,
    endDate,
    size = 10,
    page = 1,
    pagination = false,
  }) => {
    dispatch(
      setMaterialRequestHistoryLoading({
        status: pagination ? "paginating" : "loading",
      }),
    );

    const fallback = { size, page };

    try {
      const response = await instance.get(back.getMaterialRequestHistory, {
        params: buildRequestHistoryParams({
          contractNumber,
          clientNumber,
          labellerCode,
          startDate,
          endDate,
          size,
          page,
        }),
      });

      const nextState = buildRequestHistorySuccessState(response, fallback);

      dispatch(setMaterialRequestHistoryResults(nextState));
      return nextState;
    } catch (error) {
      const errorState = buildRequestHistoryErrorState(error, fallback);

      dispatch(setMaterialRequestHistoryResults(errorState));
      return errorState;
    }
  };

  return {
    materialLimit,
    fetchMaterialLimit,
    resetMaterialLimit,
    createMaterialRequest,
    fetchRequestHistory,
  };
};
